import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { NavSection } from '@/app/shared/nav/nav-section.model';
import { ShellStore } from './shell.store';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="sidebar" [class.collapsed]="collapsed()">
      <!-- Spacer que alinea el sidebar con el header principal -->
      <div class="sidebar-logo">
        <!--
          Logo Colmena (marca panal):
          Descomenta la siguiente línea y elimina la de header.component.ts al activar.

          <div class="logo-mark" aria-hidden="true">
            <svg viewBox="1 0 32 32" xmlns="http://www.w3.org/2000/svg">
              <polygon points="17,0 24.8,4.5 24.8,13.5 17,18 9.2,13.5 9.2,4.5"
                       fill="#0078C8" stroke="white" stroke-width="1.2" stroke-linejoin="round"/>
              <polygon points="9.2,13.5 17,18 17,27 9.2,31.5 1.4,27 1.4,18"
                       fill="#0078C8" stroke="white" stroke-width="1.2" stroke-linejoin="round"/>
              <polygon points="24.8,13.5 32.6,18 32.6,27 24.8,31.5 17,27 17,18"
                       fill="#0078C8" stroke="white" stroke-width="1.2" stroke-linejoin="round"/>
            </svg>
          </div>
        -->
      </div>

      <!-- Drill-in header: aparece al navegar dentro de un sub-menú -->
      @if (!collapsed() && store.drilledItem() !== null) {
        <div class="drill-header">
          <button
            class="back-btn"
            (click)="store.drillOut()"
            aria-label="Volver al menú principal"
          >
            <span
              class="back-icon"
              [innerHTML]="icons.chevronLeft | safeHtml"
              aria-hidden="true"
            ></span>
            Menú principal
          </button>
          <span class="drill-title">{{
            store.drilledItem()?.drillTitle ?? store.drilledItem()?.label
          }}</span>
        </div>
      }

      <!-- Navigation body -->
      <div class="nav-body">
        @if (store.drilledItem() === null) {
          <!-- ── MENÚ PRINCIPAL ── -->
          <div
            class="nav-menu"
            [class.nav-slide-out]="store.animDir() === 'out'"
          >
            @for (section of sections(); track section.title) {
              <div class="nav-section">
                @if (!collapsed()) {
                  <span class="section-label">{{ section.title }}</span>
                }
                @for (item of section.items; track item.label) {
                  @if (item.children?.length) {
                    <button
                      class="nav-item"
                      [class.active]="store.isItemActive(item)"
                      (click)="store.drillIn(item)"
                      [title]="item.label"
                      aria-haspopup="true"
                    >
                      @if (item.icon) {
                        <span
                          class="nav-icon"
                          [innerHTML]="item.icon | safeHtml"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <span class="nav-dot" aria-hidden="true"></span>
                      }
                      @if (!collapsed()) {
                        <span class="nav-label">{{ item.label }}</span>
                        <span
                          class="nav-chevron"
                          [innerHTML]="icons.chevronRight | safeHtml"
                          aria-hidden="true"
                        ></span>
                      }
                    </button>
                  } @else {
                    <a
                      class="nav-item"
                      [routerLink]="item.route"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="{ exact: item.route === '/' }"
                      [title]="item.label"
                    >
                      @if (item.icon) {
                        <span
                          class="nav-icon"
                          [innerHTML]="item.icon | safeHtml"
                          aria-hidden="true"
                        ></span>
                      } @else {
                        <span class="nav-dot" aria-hidden="true"></span>
                      }
                      @if (!collapsed()) {
                        <span class="nav-label">{{ item.label }}</span>
                      }
                    </a>
                  }
                }
              </div>
            }

            @if (!sections().length) {
              <p class="nav-empty">Sin secciones configuradas</p>
            }
          </div>
        } @else {
          <!-- ── SUB-MENÚ ── -->
          <div class="nav-menu" [class.nav-slide-in]="store.animDir() === 'in'">
            @for (
              item of store.drilledItem()!.children ?? [];
              track item.label
            ) {
              @if (item.children?.length) {
                <button
                  class="nav-item"
                  [class.active]="store.isItemActive(item)"
                  (click)="store.drillIn(item)"
                  [title]="item.label"
                  aria-haspopup="true"
                >
                  @if (item.icon) {
                    <span
                      class="nav-icon"
                      [innerHTML]="item.icon | safeHtml"
                      aria-hidden="true"
                    ></span>
                  } @else {
                    <span class="nav-dot" aria-hidden="true"></span>
                  }
                  @if (!collapsed()) {
                    <span class="nav-label">{{ item.label }}</span>
                    <span
                      class="nav-chevron"
                      [innerHTML]="icons.chevronRight | safeHtml"
                      aria-hidden="true"
                    ></span>
                  }
                </button>
              } @else {
                <a
                  class="nav-item"
                  [routerLink]="item.route"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: item.route === '/' }"
                  [title]="item.label"
                >
                  @if (item.icon) {
                    <span
                      class="nav-icon"
                      [innerHTML]="item.icon | safeHtml"
                      aria-hidden="true"
                    ></span>
                  } @else {
                    <span class="nav-dot" aria-hidden="true"></span>
                  }
                  @if (!collapsed()) {
                    <span class="nav-label">{{ item.label }}</span>
                  }
                </a>
              }
            }
          </div>
        }
      </div>
    </nav>
  `,
  styles: [
    `
      :host {
        display: block;
        grid-area: sidebar;
        min-width: 0;
        min-height: 0;
      }

      /* ── Shell ──────────────────────────────────────────── */
      .sidebar {
        grid-area: sidebar;
        background: var(--surface);
        border-right: 1px solid var(--border);
        overflow-y: auto;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        transition: width 200ms ease;
      }

      /* ── Logo / spacer de alineación ─────────────────────── */
      .sidebar-logo {
        display: flex;
        align-items: center;
        padding: 0 1rem;
        height: var(--header-h);
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
      }

      /* ── Drill header ───────────────────────────────────── */
      .drill-header {
        padding: 0.625rem 0.625rem 0;
        border-bottom: 1px solid var(--border);
        flex-shrink: 0;
      }
      .back-btn {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--font);
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--muted);
        padding: 0.375rem 0.5rem;
        border-radius: var(--radius-xs);
        width: 100%;
        text-align: left;
        transition:
          color 120ms,
          background 120ms;
      }
      .back-btn:hover {
        color: var(--primary);
        background: var(--surface-2);
      }
      .back-icon {
        display: flex;
        align-items: center;
      }
      .back-icon ::ng-deep svg {
        width: 0.875rem;
        height: 0.875rem;
        stroke: currentColor;
      }
      .drill-title {
        display: block;
        font-size: 0.6875rem;
        font-weight: 700;
        color: var(--text);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        padding: 0.5rem 0.75rem 0.625rem;
      }

      /* ── Nav body ───────────────────────────────────────── */
      .nav-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0.75rem 0.625rem;
      }
      .nav-section {
        padding-bottom: 0.5rem;
      }
      .section-label {
        display: block;
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--muted);
        padding: 0.625rem 0.75rem 0.375rem;
      }

      /* ── Nav items ──────────────────────────────────────── */
      .nav-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.625rem 0.875rem;
        border-radius: var(--radius-sm);
        color: var(--muted);
        text-decoration: none;
        font-size: 0.9375rem;
        font-weight: 400;
        white-space: nowrap;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        cursor: pointer;
        font-family: var(--font);
        transition:
          background 120ms,
          color 120ms;
      }
      .nav-item:hover {
        background: var(--surface-2);
        color: var(--primary);
      }
      .nav-item.active {
        background: var(--surface-3);
        color: var(--primary);
        font-weight: 600;
      }
      .nav-label {
        flex: 1;
      }

      .nav-icon {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.25rem;
        height: 1.25rem;
      }
      .nav-icon ::ng-deep svg {
        width: 1.125rem;
        height: 1.125rem;
        stroke: currentColor;
      }

      .nav-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
        flex-shrink: 0;
        margin-left: 2px;
        opacity: 0.4;
        transition: opacity 120ms;
      }
      .nav-item.active .nav-dot,
      .nav-item:hover .nav-dot {
        opacity: 1;
      }

      .nav-chevron {
        margin-left: auto;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        opacity: 0.35;
        transition:
          opacity 120ms,
          transform 120ms;
      }
      .nav-chevron ::ng-deep svg {
        width: 0.875rem;
        height: 0.875rem;
        stroke: currentColor;
      }
      .nav-item:hover .nav-chevron {
        opacity: 0.7;
        transform: translateX(2px);
      }
      .nav-item.active .nav-chevron {
        opacity: 0.8;
      }

      .nav-empty {
        font-size: 0.8125rem;
        color: var(--muted);
        padding: 1rem 1.25rem;
        margin: 0;
      }

      /* ── Slide animations ───────────────────────────────── */
      @keyframes slideFromRight {
        from {
          transform: translateX(18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideFromLeft {
        from {
          transform: translateX(-18px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      .nav-slide-in {
        animation: slideFromRight 180ms ease;
      }
      .nav-slide-out {
        animation: slideFromLeft 180ms ease;
      }

      /* ── Collapsed state ────────────────────────────────── */
      .collapsed .nav-item {
        justify-content: center;
        padding: 0.625rem;
      }
      .collapsed .section-label {
        display: none;
      }
      .collapsed .nav-section {
        padding-bottom: 0.25rem;
      }
      .collapsed .sidebar-logo {
        justify-content: center;
        padding: 0;
      }
      .collapsed .nav-chevron {
        display: none;
      }
    `,
  ],
})
export class SidebarComponent {
  readonly collapsed = input(false);
  readonly sections = input<NavSection[]>([]);
  protected readonly store = inject(ShellStore);
  protected readonly icons = NavIcons;
}
